import { useEffect, useState } from 'react';
import { DetailShell } from '../../detail/DetailShell';
import { SectionTitle } from '../../detail/SectionTitle';
import { getArticlesTracker, getActiveContacts } from '@/services/mockData';

const TOPICS = ['Finance', 'CIO', 'COO', 'Operations', 'Strategy', 'Growth', 'General'];

const TOPIC_STYLE = {
  Finance:    { bg: 'var(--pos-soft)',    border: 'var(--pos)'     },
  CIO:        { bg: 'var(--accent-soft)', border: 'var(--accent)'  },
  COO:        { bg: 'var(--warn-soft)',   border: 'var(--warn)'    },
  Operations: { bg: 'var(--paper-2)',     border: 'var(--ink-3)'   },
  Strategy:   { bg: 'var(--paper-2)',     border: 'var(--hairline)'},
  Growth:     { bg: 'var(--paper-2)',     border: 'var(--ink-3)'   },
  General:    { bg: 'var(--paper-2)',     border: 'var(--hairline)'},
};

const MONO_LABEL = {
  fontFamily: 'var(--mono)', fontSize: 8, textTransform: 'uppercase',
  letterSpacing: '0.06em', color: 'var(--ink-4)', marginBottom: 2,
};

const INPUT = {
  fontFamily: 'var(--sans)', fontSize: 10, color: 'var(--ink)',
  background: 'var(--paper)', border: '1px solid var(--hairline)',
  padding: '3px 6px', outline: 'none', boxSizing: 'border-box',
};

const BTN_LOG = {
  fontFamily: 'var(--mono)', fontSize: 8, letterSpacing: '0.06em', textTransform: 'uppercase',
  color: 'var(--accent)', background: 'transparent', border: '1px solid var(--accent)',
  padding: '2px 8px', cursor: 'pointer',
};

const BTN_ADD = {
  fontFamily: 'var(--mono)', fontSize: 8, letterSpacing: '0.06em', textTransform: 'uppercase',
  color: 'var(--paper)', background: 'var(--accent)', border: '1px solid var(--accent)',
  padding: '2px 8px', cursor: 'pointer', flexShrink: 0,
};

const BTN_CANCEL = {
  fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--ink-4)',
  background: 'transparent', border: 'none', cursor: 'pointer',
  padding: 0, lineHeight: 1, flexShrink: 0,
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtShortDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}`;
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function twoWeeksOut() {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return d.toISOString().slice(0, 10);
}

function shortTitle(title) {
  return title.length > 24 ? title.slice(0, 23) + '…' : title;
}

function uniqueContacts(articles) {
  const names = new Set();
  articles.forEach((a) => a.sends.forEach((s) => s.contact && names.add(s.contact)));
  return names.size;
}

// ── Small components ──────────────────────────────────────────────────────────

function LevelChip({ level }) {
  return (
    <span className="chip muted" style={{ fontFamily: 'var(--mono)', fontSize: 9, padding: '1px 5px', letterSpacing: '0.04em', flexShrink: 0 }}>
      {level}
    </span>
  );
}

function TopicChip({ topic }) {
  const s = TOPIC_STYLE[topic] ?? TOPIC_STYLE.General;
  return (
    <span style={{
      fontFamily: 'var(--mono)', fontSize: 8, letterSpacing: '0.06em', textTransform: 'uppercase',
      background: s.bg, border: `1px solid ${s.border}`, padding: '1px 5px', flexShrink: 0,
    }}>
      {topic}
    </span>
  );
}

// ── Contact send card ─────────────────────────────────────────────────────────

function ContactSendCard({ contact, articles, onAddSend, onRemoveSend, onSetNext, onClearNext }) {
  const [logOpen, setLogOpen] = useState(false);
  const [articleId, setArticleId] = useState('');
  const [sendDate, setSendDate] = useState(today());
  const [planOpen, setPlanOpen] = useState(false);
  const [planArticleId, setPlanArticleId] = useState('');
  const [planDate, setPlanDate] = useState('');
  const [sentOpen, setSentOpen] = useState(false);

  // Compute what this contact has received across all articles
  const received = articles
    .flatMap((a) =>
      a.sends
        .filter((s) => s.contact === contact.connect)
        .map((s) => ({ ...s, articleId: a.id, articleTitle: a.title, topic: a.topic }))
    )
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const nextSend = contact.next_send ?? null;
  const nextArticle = nextSend ? articles.find((a) => a.id === nextSend.articleId) : null;
  const nextArticleTitle = nextArticle?.title ?? '—';

  const handleAdd = () => {
    if (!articleId) return;
    onAddSend(articleId, contact.connect, contact.firm, sendDate);
    setLogOpen(false);
    setArticleId('');
    setSendDate(today());
  };

  const openPlan = () => {
    setPlanArticleId(nextSend?.articleId ?? '');
    setPlanDate(nextSend?.by ?? twoWeeksOut());
    setLogOpen(false);
    setPlanOpen(true);
  };

  const handleSavePlan = () => {
    if (!planArticleId || !planDate) return;
    onSetNext(contact.id, planArticleId, planDate);
    setPlanOpen(false);
  };

  return (
    <div style={{ border: '1px solid var(--hairline)', padding: '8px 10px', background: 'var(--paper)' }}>
      {/* Contact header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.2 }}>
            {contact.connect_url ? (
              <a href={contact.connect_url} target="_blank" rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'none' }}>
                {contact.connect || '—'}
                <span style={{ marginLeft: 2, fontSize: 7, opacity: 0.45 }}>↗</span>
              </a>
            ) : (contact.connect || '—')}
          </div>
          <div style={{ fontSize: 9, color: 'var(--ink-4)', lineHeight: 1.3 }}>{contact.position}</div>
          <div style={{ fontSize: 9, color: 'var(--ink-3)', lineHeight: 1.3 }}>
            {contact.firm_url ? (
              <a href={contact.firm_url} target="_blank" rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'none' }}>
                {contact.firm}
                <span style={{ marginLeft: 2, fontSize: 7, opacity: 0.45 }}>↗</span>
              </a>
            ) : contact.firm}
          </div>
        </div>
        <LevelChip level={contact.connect_level} />
      </div>

      {/* Next send plan */}
      <div style={{ marginBottom: 6 }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 7, color: 'var(--ink-4)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 3 }}>Next</div>
        {planOpen ? (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={planArticleId}
              onChange={(e) => setPlanArticleId(e.target.value)}
              style={{ ...INPUT, flex: 1, minWidth: 120 }}
            >
              <option value="">Select article…</option>
              {articles.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
            </select>
            <input
              type="date"
              value={planDate}
              onChange={(e) => setPlanDate(e.target.value)}
              style={{ ...INPUT, width: 116, fontFamily: 'var(--mono)', fontSize: 9 }}
            />
            <button type="button" onClick={handleSavePlan} style={BTN_ADD}>SET</button>
            <button type="button" onClick={() => setPlanOpen(false)} style={BTN_CANCEL}>×</button>
          </div>
        ) : nextSend ? (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span
              onClick={openPlan}
              style={{
                fontFamily: 'var(--mono)', fontSize: 8, cursor: 'pointer',
                background: 'var(--paper-2)', border: '1px solid var(--hairline)',
                padding: '1px 5px', color: 'var(--ink-2)',
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}
            >
              {shortTitle(nextArticleTitle)} · {fmtShortDate(nextSend.by)}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onClearNext(contact.id); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 9, lineHeight: 1, color: 'inherit', opacity: 0.55 }}
              >×</button>
            </span>
            <button
              type="button"
              onClick={() => { onAddSend(nextSend.articleId, contact.connect, contact.firm, today()); onClearNext(contact.id); }}
              style={{
                fontFamily: 'var(--mono)', fontSize: 7, letterSpacing: '0.06em', textTransform: 'uppercase',
                color: 'var(--ink-4)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
              }}
            >log</button>
          </div>
        ) : (
          <span
            onClick={openPlan}
            style={{ fontFamily: 'var(--mono)', fontSize: 8, cursor: 'pointer', color: 'var(--ink-4)', letterSpacing: '0.05em' }}
          >
            — SET PLAN
          </span>
        )}
      </div>

      {/* Sent history — accordion */}
      <div style={{ marginBottom: 6 }}>
        <button
          type="button"
          onClick={() => setSentOpen((v) => !v)}
          style={{
            fontFamily: 'var(--mono)', fontSize: 7, letterSpacing: '0.06em', textTransform: 'uppercase',
            color: 'var(--ink-4)', background: 'transparent', border: 'none', cursor: 'pointer',
            padding: 0, display: 'flex', alignItems: 'center', gap: 4, marginBottom: sentOpen ? 4 : 0,
          }}
        >
          Sent{received.length > 0 ? ` (${received.length})` : ''} {sentOpen ? '▴' : '▾'}
        </button>
        {sentOpen && (
          received.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {received.map((r) => (
                <span
                  key={r.id}
                  style={{
                    fontFamily: 'var(--mono)', fontSize: 8,
                    background: 'var(--accent-soft)', border: '1px solid var(--accent)',
                    padding: '1px 4px', color: 'var(--ink-2)',
                    display: 'inline-flex', alignItems: 'center', gap: 3,
                  }}
                >
                  {shortTitle(r.articleTitle)} · {fmtShortDate(r.date)}
                  <button
                    type="button"
                    onClick={() => onRemoveSend(r.articleId, r.id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 9, lineHeight: 1, color: 'inherit', opacity: 0.55 }}
                  >×</button>
                </span>
              ))}
            </div>
          ) : (
            <div style={{ fontFamily: 'var(--mono)', fontSize: 8, color: 'var(--ink-4)', letterSpacing: '0.05em' }}>
              NO ARTICLES SENT YET
            </div>
          )
        )}
      </div>

      {/* Log send — hidden by default */}
      {logOpen ? (
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', borderTop: '1px solid var(--hairline)', paddingTop: 6 }}>
          <select
            value={articleId}
            onChange={(e) => setArticleId(e.target.value)}
            style={{ ...INPUT, flex: 1, minWidth: 120 }}
          >
            <option value="">Select article…</option>
            {articles.map((a) => (
              <option key={a.id} value={a.id}>{a.title}</option>
            ))}
          </select>
          <input
            type="date"
            value={sendDate}
            onChange={(e) => setSendDate(e.target.value)}
            style={{ ...INPUT, width: 116, fontFamily: 'var(--mono)', fontSize: 9 }}
          />
          <button type="button" onClick={handleAdd} style={BTN_ADD}>ADD</button>
          <button type="button" onClick={() => setLogOpen(false)} style={BTN_CANCEL}>×</button>
        </div>
      ) : (
        <div style={{ borderTop: '1px solid var(--hairline)', paddingTop: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => { setPlanOpen(false); setLogOpen(true); }}
            style={{
              fontFamily: 'var(--mono)', fontSize: 7, letterSpacing: '0.06em', textTransform: 'uppercase',
              color: 'var(--ink-4)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
            }}
          >
            + log send
          </button>
        </div>
      )}
    </div>
  );
}

// ── Article library row ───────────────────────────────────────────────────────

function ArticleLibraryRow({ article }) {
  const count = article.sends.length;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, paddingBottom: 5, borderBottom: '1px solid var(--hairline)' }}>
      <TopicChip topic={article.topic} />
      <div style={{ flex: 1, fontSize: 10, lineHeight: 1.35, color: 'var(--ink-2)' }}>
        {article.url ? (
          <a href={article.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
            {article.title}<span style={{ marginLeft: 2, fontSize: 7, opacity: 0.45 }}>↗</span>
          </a>
        ) : article.title}
      </div>
      <span style={{
        fontFamily: 'var(--mono)', fontSize: 9, flexShrink: 0,
        background: count > 0 ? 'var(--pos-soft)' : 'var(--paper-2)',
        border: `1px solid ${count > 0 ? 'var(--pos)' : 'var(--hairline)'}`,
        padding: '1px 6px', minWidth: 22, textAlign: 'center',
      }}>
        {count}
      </span>
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

export function ArticlesDetail({ onClose }) {
  const [articles, setArticles] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [addingArticle, setAddingArticle] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newTopic, setNewTopic] = useState('General');

  useEffect(() => {
    let cancelled = false;
    Promise.all([getArticlesTracker(), getActiveContacts()]).then(([arts, cons]) => {
      if (!cancelled) {
        setArticles(arts);
        setContacts(cons);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const addSend = (articleId, contactName, firm, date) => {
    setArticles((prev) =>
      prev.map((a) =>
        a.id === articleId
          ? { ...a, sends: [{ id: `s_${Date.now()}`, contact: contactName, firm, date }, ...a.sends] }
          : a
      )
    );
  };

  const removeSend = (articleId, sendId) => {
    setArticles((prev) =>
      prev.map((a) =>
        a.id === articleId
          ? { ...a, sends: a.sends.filter((s) => s.id !== sendId) }
          : a
      )
    );
  };

  const setNextSend = (contactId, articleId, by) => {
    setContacts((prev) =>
      prev.map((c) => c.id === contactId ? { ...c, next_send: { articleId, by } } : c)
    );
  };

  const clearNextSend = (contactId) => {
    setContacts((prev) =>
      prev.map((c) => c.id === contactId ? { ...c, next_send: null } : c)
    );
  };

  const confirmAddArticle = () => {
    if (!newTitle.trim()) return;
    setArticles((prev) => [
      { id: `art_${Date.now()}`, title: newTitle.trim(), url: '', published: today(), topic: newTopic, sends: [] },
      ...prev,
    ]);
    setNewTitle('');
    setNewTopic('General');
    setAddingArticle(false);
  };

  const sortedArticles = [...articles].sort((a, b) => b.sends.length - a.sends.length);

  const totalSends    = articles.reduce((n, a) => n + a.sends.length, 0);
  const contactsCount = uniqueContacts(articles);
  const inUse         = articles.filter((a) => a.sends.length > 0).length;

  const OVERVIEW = [
    { label: 'Articles',         value: articles.length, bg: 'var(--paper-2)',     border: 'var(--hairline)' },
    { label: 'Total sends',      value: totalSends,      bg: 'var(--accent-soft)', border: 'var(--accent)'   },
    { label: 'Contacts reached', value: contactsCount,   bg: 'var(--warn-soft)',   border: 'var(--warn)'     },
    { label: 'Articles in use',  value: inUse,           bg: 'var(--pos-soft)',    border: 'var(--pos)'      },
  ];

  return (
    <DetailShell eyebrow="02 · Pipeline Builder" title="Articles Tracker" onClose={onClose}>

      {/* Overview */}
      <div className="card">
        <SectionTitle label="Distribution summary" meta={`${articles.length} ARTICLES · ${contacts.length} CONTACTS`} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
          {OVERVIEW.map((o) => (
            <div key={o.label} style={{ border: `1px solid ${o.border}`, padding: '8px 10px', background: o.bg }}>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 4 }}>{o.label}</div>
              <div style={{ fontFamily: 'var(--serif)', fontSize: 26, lineHeight: 1 }}>{o.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Main two-column layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, alignItems: 'start' }}>

        {/* Left: Active contacts with article history */}
        <div className="card">
          <SectionTitle label="Active contacts" meta={`${contacts.length} TRACKED · ARTICLE HISTORY`} />
          <div style={{ display: 'grid', gap: 8 }}>
            {contacts.map((c) => (
              <ContactSendCard
                key={c.id}
                contact={c}
                articles={articles}
                onAddSend={addSend}
                onRemoveSend={removeSend}
                onSetNext={setNextSend}
                onClearNext={clearNextSend}
              />
            ))}
            {contacts.length === 0 && (
              <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--ink-4)', letterSpacing: '0.06em' }}>
                NO ACTIVE CONTACTS
              </div>
            )}
          </div>
        </div>

        {/* Right: Article library */}
        <div className="card" style={{ position: 'sticky', top: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <SectionTitle label="Article library" meta={`${inUse} / ${articles.length} IN USE`} />
            <button
              type="button"
              onClick={() => setAddingArticle(true)}
              style={BTN_LOG}
            >
              + NEW
            </button>
          </div>

          {/* Add article form */}
          {addingArticle && (
            <div style={{ marginBottom: 10, padding: '8px', border: '1px solid var(--hairline)', background: 'var(--paper-2)' }}>
              <div style={{ marginBottom: 6 }}>
                <div style={MONO_LABEL}>Title</div>
                <input
                  autoFocus
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') confirmAddArticle(); if (e.key === 'Escape') setAddingArticle(false); }}
                  placeholder="Article title…"
                  style={{ ...INPUT, width: '100%', display: 'block' }}
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <div style={MONO_LABEL}>Topic</div>
                <select value={newTopic} onChange={(e) => setNewTopic(e.target.value)} style={{ ...INPUT, width: '100%' }}>
                  {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button type="button" onClick={confirmAddArticle} style={BTN_ADD}>ADD ARTICLE</button>
                <button type="button" onClick={() => setAddingArticle(false)} style={{ ...BTN_CANCEL, fontSize: 9, letterSpacing: '0.04em', textTransform: 'uppercase' }}>CANCEL</button>
              </div>
            </div>
          )}

          {/* Article rows ranked by send count */}
          <div style={{ display: 'grid', gap: 5, maxHeight: 480, overflowY: 'auto' }}>
            {sortedArticles.map((a) => (
              <ArticleLibraryRow key={a.id} article={a} />
            ))}
            {articles.length === 0 && (
              <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--ink-4)', letterSpacing: '0.06em' }}>
                NO ARTICLES YET
              </div>
            )}
          </div>
        </div>

      </div>

    </DetailShell>
  );
}

export default ArticlesDetail;
